import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { formatTimezoneLabel } from '@/lib/format/timezone';
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronLeft, Medal, CheckCircle, XCircle, Clock, ShieldCheck, Loader2, Camera, Play, Search, Pencil, Save, CalendarRange, AlertTriangle, RefreshCw, Download, Copy, ClipboardCheck } from "lucide-react";
import { TimezoneSelect } from "@/components/TimezoneSelect";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useAdminChallengeQuery,
  useAdminValidateRankingMutation,
  useAdminForceStartSnapshotMutation,
  useAdminForceConsolidationMutation,
  useAdminRankingSnapshotsLazyQuery,
  useAdminUpdateSnapshotViewMutation,
  useAdminPartialRankingByDateRangeLazyQuery,
  useAdminForceUpdateChallengeClipViewsMutation,
} from "@/generated/graphql";
import { formatDateTime } from "@/lib/format";
import { useToast } from "@/hooks/use-toast";

type RankingEntry = {
  participation_id: string;
  user_id: string;
  user_name?: string;
  user_profile_picture?: string;
  total_views: string;
  clip_count: number;
};

function formatCountdown(seconds: number): string {
  const abs = Math.abs(seconds);
  const d = Math.floor(abs / 86400);
  const h = Math.floor((abs % 86400) / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

const AdminChallengeRankings = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { challengeId } = useParams<{ challengeId: string }>();
  const navigate = useNavigate();

  const [confirmTarget, setConfirmTarget] = useState<{ id: string; name: string; validated: boolean } | null>(null);
  const [forceTarget, setForceTarget] = useState<{ id: string; name: string; action: "snapshot" | "consolidation" } | null>(null);
  const [snapshotAuditRanking, setSnapshotAuditRanking] = useState<{ id: string; name: string } | null>(null);
  const [editingView, setEditingView] = useState<{ id: string; views: string } | null>(null);
  const [countdowns, setCountdowns] = useState<Record<string, number>>({});
  const [startCountdowns, setStartCountdowns] = useState<Record<string, number>>({});
  const [partialRankingOpen, setPartialRankingOpen] = useState(false);
  const [partialStartDate, setPartialStartDate] = useState("");
  const [partialEndDate, setPartialEndDate] = useState("");
  const [partialTimezone, setPartialTimezone] = useState("America/Sao_Paulo");
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data, loading, error, refetch } = useAdminChallengeQuery({
    variables: { id: challengeId! },
    skip: !challengeId,
    fetchPolicy: "cache-and-network",
  });

  const [validateRanking, { loading: validating }] = useAdminValidateRankingMutation();
  const [forceStartSnapshot, { loading: forcingSnapshot }] = useAdminForceStartSnapshotMutation();
  const [forceConsolidation, { loading: forcingConsolidation }] = useAdminForceConsolidationMutation();
  const [fetchSnapshots, { data: snapshotsData, loading: loadingSnapshots }] = useAdminRankingSnapshotsLazyQuery({ fetchPolicy: "network-only" });
  const [updateSnapshotView, { loading: updatingView }] = useAdminUpdateSnapshotViewMutation();
  const [fetchPartialRanking, { data: partialRankingData, loading: loadingPartialRanking }] = useAdminPartialRankingByDateRangeLazyQuery({ fetchPolicy: "network-only" });
  const [forceUpdateClipViews] = useAdminForceUpdateChallengeClipViewsMutation();
  const [syncingViews, setSyncingViews] = useState(false);

  const challenge = data?.adminChallenge;
  const rankings = challenge?.rankings || [];

  // Countdown timer
  const rankingKey = rankings.map(r => `${r.id}:${r.is_consolidated}:${r.time_until_start_seconds}:${r.time_until_end_seconds}`).join(",");
  useEffect(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);

    const initialEnd: Record<string, number> = {};
    const initialStart: Record<string, number> = {};
    for (const r of rankings) {
      if (!r.is_consolidated) {
        if (r.time_until_end_seconds != null) initialEnd[r.id] = r.time_until_end_seconds;
        if ((r.time_until_start_seconds ?? 0) > 0) initialStart[r.id] = r.time_until_start_seconds!;
      }
    }
    setCountdowns(initialEnd);
    setStartCountdowns(initialStart);

    if (Object.keys(initialEnd).length > 0 || Object.keys(initialStart).length > 0) {
      countdownRef.current = setInterval(() => {
        setCountdowns((prev) => {
          const next = { ...prev };
          for (const id of Object.keys(next)) next[id] = next[id] - 1;
          return next;
        });
        setStartCountdowns((prev) => {
          const next = { ...prev };
          for (const id of Object.keys(next)) next[id] = next[id] - 1;
          return next;
        });
      }, 1000);
    }
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rankingKey]);

  const handleValidate = async (id: string, validated: boolean) => {
    try {
      await validateRanking({ variables: { id, validated } });
      toast({ title: validated ? t("admin.challenges.rankingValidated") : t("admin.challenges.rankingUnvalidated") });
      setConfirmTarget(null);
      refetch();
    } catch (err: any) {
      toast({ title: err.message, variant: "destructive" });
    }
  };

  const handleForceAction = async () => {
    if (!forceTarget) return;
    try {
      if (forceTarget.action === "snapshot") {
        await forceStartSnapshot({ variables: { rankingId: forceTarget.id } });
        toast({ title: t("admin.challenges.forceStartSnapshotSuccess") });
      } else {
        await forceConsolidation({ variables: { rankingId: forceTarget.id } });
        toast({ title: t("admin.challenges.forceConsolidationSuccess") });
      }
      setForceTarget(null);
      refetch();
    } catch (err: any) {
      toast({ title: err.message, variant: "destructive" });
    }
  };

  const openSnapshotAudit = (rankingId: string, rankingName: string) => {
    setSnapshotAuditRanking({ id: rankingId, name: rankingName });
    setEditingView(null);
    fetchSnapshots({ variables: { rankingId } });
  };

  const parseDDMMYYYY = (value: string): string | null => {
    const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return null;
    return `${match[3]}-${match[2]}-${match[1]}`;
  };

  const formatDateInput = (value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  };

  const isValidDateInput = (value: string): boolean => {
    return parseDDMMYYYY(value) !== null;
  };

  const handleFetchPartialRanking = async () => {
    if (!challengeId) return;
    const start = partialStartDate ? parseDDMMYYYY(partialStartDate) : null;
    const end = partialEndDate ? parseDDMMYYYY(partialEndDate) : null;
    // If a date is typed but invalid, don't fetch
    if (partialStartDate && !start) return;
    if (partialEndDate && !end) return;
    try {
      await fetchPartialRanking({
        variables: {
          challengeId,
          startDate: start,
          endDate: end,
          timezone: partialTimezone,
        },
      });
    } catch {
      toast({ title: t("admin.challenges.partialRankingError"), variant: "destructive" });
    }
  };

  const handleSyncAndRefetch = async () => {
    if (!challengeId) return;
    const start = partialStartDate ? parseDDMMYYYY(partialStartDate) : null;
    const end = partialEndDate ? parseDDMMYYYY(partialEndDate) : null;

    const missingClipIds = partialRankingData?.adminPartialRankingByDateRange?.clips_without_views?.map((w) => w.clip_id) || [];
    if (missingClipIds.length === 0) return;

    setSyncingViews(true);
    try {
      await forceUpdateClipViews({ variables: { clipIds: missingClipIds } });

      // Poll until clips_without_views shrinks or stabilizes
      const MAX_POLLS = 30;
      const POLL_INTERVAL = 5000;
      let prevMissing = partialRankingData?.adminPartialRankingByDateRange?.clips_without_views?.length ?? 0;
      let stableCount = 0;

      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL));
        const { data } = await fetchPartialRanking({
          variables: { challengeId, startDate: start, endDate: end, timezone: partialTimezone },
        });
        const currentMissing = data?.adminPartialRankingByDateRange?.clips_without_views?.length ?? 0;
        if (currentMissing === 0) break;
        if (currentMissing === prevMissing) {
          stableCount++;
          if (stableCount >= 3) break;
        } else {
          stableCount = 0;
        }
        prevMissing = currentMissing;
      }
    } catch {
      toast({ title: t("admin.challenges.forceUpdateAllViewsError"), variant: "destructive" });
    } finally {
      setSyncingViews(false);
    }
  };

  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  const getPartialRankingRows = () => {
    const entries = partialRankingData?.adminPartialRankingByDateRange?.entries || [];
    return entries.map((entry, i) => ({
      "#": i + 1,
      [t("admin.challenges.participant")]: entry.user_name || "—",
      [t("admin.challenges.clipCount")]: entry.clip_count,
      [t("admin.challenges.totalViews")]: parseInt(entry.total_views || "0", 10),
    }));
  };

  const handleExportXLSX = () => {
    const rows = getPartialRankingRows();
    if (rows.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ranking");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "partial-ranking.xlsx");
  };

  const handleExportCSV = () => {
    const rows = getPartialRankingRows();
    if (rows.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(rows);
    const csv = XLSX.utils.sheet_to_csv(ws);
    saveAs(new Blob([csv], { type: "text/csv;charset=utf-8" }), "partial-ranking.csv");
  };

  const handleCopyToClipboard = async () => {
    const rows = getPartialRankingRows();
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const lines = [headers.join("\t"), ...rows.map((r) => Object.values(r).join("\t"))];
    await navigator.clipboard.writeText(lines.join("\n"));
    setCopiedToClipboard(true);
    setTimeout(() => setCopiedToClipboard(false), 2000);
  };

  const handleUpdateView = async () => {
    if (!editingView) return;
    try {
      await updateSnapshotView({ variables: { id: editingView.id, views: editingView.views } });
      toast({ title: t("admin.challenges.viewsUpdated") });
      setEditingView(null);
      if (snapshotAuditRanking) {
        fetchSnapshots({ variables: { rankingId: snapshotAuditRanking.id } });
      }
      refetch();
    } catch {
      toast({ title: t("admin.challenges.viewsUpdateError"), variant: "destructive" });
    }
  };

  const snapshots = snapshotsData?.adminRankingSnapshots;

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate("/admin/challenges")}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                {t("common.back")}
              </Button>
              <div className="flex items-center gap-2">
                <Medal className="w-6 h-6 text-primary" />
                <div>
                  <h1 className="text-xl font-bold">{t("admin.challenges.rankingValidation")}</h1>
                  {challenge && <p className="text-sm text-muted-foreground">{challenge.name}</p>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPartialStartDate("");
                  setPartialEndDate("");
                  setPartialRankingOpen(true);
                }}
              >
                <CalendarRange className="w-4 h-4 mr-1" />
                {t("admin.challenges.partialRankingExtract")}
              </Button>
              <Badge variant="outline" className="text-red-500 border-red-500">ADMIN</Badge>
            </div>
          </div>

          {loading && !data ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">{t("admin.common.error")}</div>
          ) : rankings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">{t("admin.common.noResults")}</div>
          ) : (
            <div className="space-y-6">
              {rankings.map((ranking) => {
                const consolidatedData = (ranking.ranking_data as RankingEntry[] | null) || [];
                const partialData = (ranking.partial_ranking || []) as RankingEntry[];
                const isFinalized = ranking.is_consolidated && ranking.admin_validated;
                const displayData = isFinalized ? consolidatedData : partialData;
                const countdown = countdowns[ranking.id];
                const hasStartSnapshot = !!ranking.start_snapshotted_at;
                const isConsolidated = ranking.is_consolidated;

                return (
                  <Card key={ranking.id}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CardTitle className="text-lg">{ranking.name}</CardTitle>
                          {ranking.is_consolidated ? (
                            ranking.admin_validated ? (
                              <Badge variant="outline" className="text-emerald-500 border-emerald-500 text-xs">
                                <ShieldCheck className="w-3 h-3 mr-1" />
                                {t("admin.challenges.validated")}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-amber-500 border-amber-500 text-xs">
                                <Clock className="w-3 h-3 mr-1" />
                                {t("admin.challenges.pendingValidation")}
                              </Badge>
                            )
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground border-muted-foreground text-xs flex items-center gap-1">
                              {(startCountdowns[ranking.id] ?? 0) > 0 ? (
                                <>
                                  <Clock className="w-3 h-3" />
                                  {t("challenge.rankings.startsIn", { time: formatCountdown(startCountdowns[ranking.id]) })}
                                </>
                              ) : countdown != null && countdown > 0 ? (
                                <>
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  {t("challenge.rankings.countdown", { time: formatCountdown(countdown) })}
                                </>
                              ) : countdown != null && countdown <= 0 ? (
                                <>
                                  <Clock className="w-3 h-3" />
                                  {t("challenge.rankings.consolidating")}
                                </>
                              ) : (
                                t("admin.challenges.pending")
                              )}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Snapshot Audit button */}
                          {(hasStartSnapshot || isConsolidated) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openSnapshotAudit(ranking.id, ranking.name)}
                            >
                              <Search className="w-4 h-4 mr-1" />
                              {t("admin.challenges.snapshotAudit")}
                            </Button>
                          )}
                          {/* Force buttons */}
                          {!hasStartSnapshot && !isConsolidated && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setForceTarget({ id: ranking.id, name: ranking.name, action: "snapshot" })}
                              disabled={forcingSnapshot}
                            >
                              <Camera className="w-4 h-4 mr-1" />
                              {t("admin.challenges.forceStartSnapshot")}
                            </Button>
                          )}
                          {!isConsolidated && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setForceTarget({ id: ranking.id, name: ranking.name, action: "consolidation" })}
                              disabled={forcingConsolidation}
                            >
                              <Play className="w-4 h-4 mr-1" />
                              {t("admin.challenges.forceConsolidation")}
                            </Button>
                          )}
                          {ranking.is_consolidated && (
                            ranking.admin_validated ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-amber-500 border-amber-500/50"
                                onClick={() => setConfirmTarget({ id: ranking.id, name: ranking.name, validated: false })}
                                disabled={validating}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                {t("admin.challenges.unvalidate")}
                              </Button>
                            ) : (
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => setConfirmTarget({ id: ranking.id, name: ranking.name, validated: true })}
                                disabled={validating}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                {t("admin.challenges.validate")}
                              </Button>
                            )
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm mb-4">
                        <div>
                          <p className="text-muted-foreground">{t("admin.challenges.prizeBuilder.timezone")}</p>
                          <p className="font-medium">{formatTimezoneLabel(ranking.timezone)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">{t("admin.challenges.prizeBuilder.startDate")}</p>
                          <p className="font-medium">{ranking.start_date ? formatDateTime(ranking.start_date) : "—"}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">{t("admin.challenges.prizeBuilder.endDate")}</p>
                          <p className="font-medium">{ranking.end_date ? formatDateTime(ranking.end_date) : "—"}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">{t("admin.challenges.consolidated")}</p>
                          <p className="font-medium">
                            {ranking.consolidated_at ? formatDateTime(ranking.consolidated_at) : "—"}
                          </p>
                        </div>
                      </div>

                      {displayData.length > 0 ? (
                        <>
                          {!isFinalized && (
                            <p className="text-xs text-muted-foreground mb-2 italic">
                              {ranking.is_consolidated
                                ? t("admin.challenges.consolidatedNotValidated")
                                : t("admin.challenges.partialRankingHint")}
                            </p>
                          )}
                          <div className="rounded-md border">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="w-[60px]">#</TableHead>
                                  <TableHead>{t("admin.challenges.participant")}</TableHead>
                                  <TableHead>{t("admin.challenges.clipCount")}</TableHead>
                                  <TableHead>{t("admin.challenges.totalViews")}</TableHead>
                                  <TableHead className="w-[100px]">{t("admin.challenges.actions")}</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {displayData.map((entry, i) => (
                                  <TableRow key={entry.participation_id}>
                                    <TableCell>
                                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                                        i === 0 ? "bg-amber-500 text-black" :
                                        i === 1 ? "bg-gray-400 text-black" :
                                        i === 2 ? "bg-amber-700 text-white" :
                                        "bg-muted text-muted-foreground"
                                      }`}>
                                        {i + 1}
                                      </span>
                                    </TableCell>
                                    <TableCell>
                                      <span className="font-medium">{entry.user_name || "—"}</span>
                                    </TableCell>
                                    <TableCell>{entry.clip_count}</TableCell>
                                    <TableCell className="font-bold text-primary">
                                      {parseInt(entry.total_views || "0", 10).toLocaleString()}
                                    </TableCell>
                                    <TableCell>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 px-2 text-xs text-primary"
                                        onClick={() => navigate(`/admin/challenges/${challengeId}/participants/${entry.participation_id}/audit`)}
                                      >
                                        {t("admin.challenges.audit")}
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">{t("admin.common.noResults")}</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Confirm validate/unvalidate dialog */}
      <AlertDialog open={!!confirmTarget} onOpenChange={(open) => !open && setConfirmTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmTarget?.validated
                ? t("admin.challenges.validateConfirmTitle")
                : t("admin.challenges.unvalidateConfirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmTarget?.validated
                ? t("admin.challenges.validateConfirmDescription", { name: confirmTarget?.name })
                : t("admin.challenges.unvalidateConfirmDescription", { name: confirmTarget?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmTarget && handleValidate(confirmTarget.id, confirmTarget.validated)}
              disabled={validating}
            >
              {confirmTarget?.validated ? t("admin.challenges.validate") : t("admin.challenges.unvalidate")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm force snapshot/consolidation dialog */}
      <AlertDialog open={!!forceTarget} onOpenChange={(open) => !open && setForceTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {forceTarget?.action === "snapshot"
                ? t("admin.challenges.forceStartSnapshotConfirmTitle")
                : t("admin.challenges.forceConsolidationConfirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {forceTarget?.action === "snapshot"
                ? t("admin.challenges.forceStartSnapshotConfirmDescription", { name: forceTarget?.name })
                : t("admin.challenges.forceConsolidationConfirmDescription", { name: forceTarget?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleForceAction}
              disabled={forcingSnapshot || forcingConsolidation}
            >
              {forceTarget?.action === "snapshot"
                ? t("admin.challenges.forceStartSnapshot")
                : t("admin.challenges.forceConsolidation")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Snapshot Audit Dialog */}
      <Dialog open={!!snapshotAuditRanking} onOpenChange={(open) => !open && setSnapshotAuditRanking(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t("admin.challenges.snapshotAuditFor", { name: snapshotAuditRanking?.name })}
            </DialogTitle>
          </DialogHeader>

          {loadingSnapshots ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : snapshots ? (
            <Tabs defaultValue="start">
              <TabsList>
                <TabsTrigger value="start">
                  {t("admin.challenges.startSnapshot")} ({snapshots.start_views.length})
                </TabsTrigger>
                <TabsTrigger value="end">
                  {t("admin.challenges.endSnapshot")} ({snapshots.end_views.length})
                </TabsTrigger>
              </TabsList>

              {(["start", "end"] as const).map((type) => {
                const views = type === "start" ? snapshots.start_views : snapshots.end_views;
                return (
                  <TabsContent key={type} value={type}>
                    {views.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-6">{t("admin.challenges.noSnapshots")}</p>
                    ) : (
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{t("admin.challenges.participant")}</TableHead>
                              <TableHead>{t("admin.challenges.platform")}</TableHead>
                              <TableHead>{t("admin.challenges.clipUrl")}</TableHead>
                              <TableHead>{t("admin.challenges.views")}</TableHead>
                              <TableHead className="w-[120px]">{t("admin.challenges.actions")}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {views.map((v) => (
                              <TableRow key={v.snapshot_view_id}>
                                <TableCell className="text-sm">{v.participant_name || "—"}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="text-xs">{v.clip_platform}</Badge>
                                </TableCell>
                                <TableCell>
                                  <a
                                    href={v.clip_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-primary hover:underline truncate block max-w-[200px]"
                                  >
                                    {v.clip_url}
                                  </a>
                                </TableCell>
                                <TableCell>
                                  {editingView?.id === v.snapshot_view_id ? (
                                    <Input
                                      type="number"
                                      className="h-7 w-24 text-sm"
                                      value={editingView.views}
                                      onChange={(e) => setEditingView({ ...editingView, views: e.target.value })}
                                      onKeyDown={(e) => e.key === "Enter" && handleUpdateView()}
                                    />
                                  ) : (
                                    <span className="font-bold">{parseInt(v.views || "0", 10).toLocaleString()}</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {editingView?.id === v.snapshot_view_id ? (
                                    <div className="flex items-center gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 px-2"
                                        onClick={handleUpdateView}
                                        disabled={updatingView}
                                      >
                                        <Save className="w-3 h-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 px-2"
                                        onClick={() => setEditingView(null)}
                                      >
                                        <XCircle className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 px-2 text-xs"
                                      onClick={() => setEditingView({ id: v.snapshot_view_id, views: v.views })}
                                    >
                                      <Pencil className="w-3 h-3 mr-1" />
                                      {t("admin.challenges.editViews")}
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </TabsContent>
                );
              })}
            </Tabs>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Partial Ranking by Date Range Modal */}
      <Dialog open={partialRankingOpen} onOpenChange={(open) => !open && setPartialRankingOpen(false)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("admin.challenges.partialRankingExtract")}</DialogTitle>
            <DialogDescription>{t("admin.challenges.partialRankingExtractDesc")}</DialogDescription>
          </DialogHeader>

          <div className="flex items-end gap-4 mt-2 flex-wrap">
            <div className="flex-1 min-w-[130px]">
              <Label>{t("admin.challenges.startDate")}</Label>
              <Input
                type="text"
                placeholder="DD/MM/YYYY"
                value={partialStartDate}
                onChange={(e) => setPartialStartDate(formatDateInput(e.target.value))}
              />
            </div>
            <div className="flex-1 min-w-[130px]">
              <Label>{t("admin.challenges.endDate")}</Label>
              <Input
                type="text"
                placeholder="DD/MM/YYYY"
                value={partialEndDate}
                onChange={(e) => setPartialEndDate(formatDateInput(e.target.value))}
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label>{t("admin.challenges.prizeBuilder.timezone")}</Label>
              <TimezoneSelect value={partialTimezone} onChange={setPartialTimezone} />
            </div>
            <Button
              onClick={handleFetchPartialRanking}
              disabled={(partialStartDate !== "" && !isValidDateInput(partialStartDate)) || (partialEndDate !== "" && !isValidDateInput(partialEndDate)) || loadingPartialRanking}
            >
              {loadingPartialRanking ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-1" />
              )}
              {t("admin.challenges.partialRankingExtract")}
            </Button>
          </div>

          {partialRankingData?.adminPartialRankingByDateRange && (() => {
            const entries = partialRankingData.adminPartialRankingByDateRange.entries;
            const totalViews = entries.reduce((sum, e) => sum + parseInt(e.total_views || "0", 10), 0);
            const totalClips = entries.reduce((sum, e) => sum + e.clip_count, 0);
            const totalParticipants = entries.length;
            return (
            <div className="mt-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                {t("admin.challenges.partialRankingPeriod", {
                  start: partialRankingData.adminPartialRankingByDateRange.start_date_used || "—",
                  end: partialRankingData.adminPartialRankingByDateRange.end_date_used || "—",
                })}
              </p>

              <div className="grid grid-cols-3 gap-4">
                <div className="rounded-md border p-3 text-center">
                  <p className="text-2xl font-bold text-primary">{totalParticipants}</p>
                  <p className="text-xs text-muted-foreground">{t("admin.challenges.participants")}</p>
                </div>
                <div className="rounded-md border p-3 text-center">
                  <p className="text-2xl font-bold text-primary">{totalClips}</p>
                  <p className="text-xs text-muted-foreground">{t("admin.challenges.clipCount")}</p>
                </div>
                <div className="rounded-md border p-3 text-center">
                  <p className="text-2xl font-bold text-primary">{totalViews.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">{t("admin.challenges.totalViews")}</p>
                </div>
              </div>

              {entries.length > 0 && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleExportXLSX}>
                    <Download className="w-3 h-3 mr-1" />
                    XLSX
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleExportCSV}>
                    <Download className="w-3 h-3 mr-1" />
                    CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleCopyToClipboard}>
                    {copiedToClipboard ? (
                      <ClipboardCheck className="w-3 h-3 mr-1" />
                    ) : (
                      <Copy className="w-3 h-3 mr-1" />
                    )}
                    {copiedToClipboard ? t("admin.challenges.copiedToClipboard") : t("admin.challenges.copyToClipboard")}
                  </Button>
                </div>
              )}

              {partialRankingData.adminPartialRankingByDateRange.clips_without_views.length > 0 && (
                <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-amber-600 text-sm font-medium">
                      <AlertTriangle className="w-4 h-4" />
                      {t("admin.challenges.partialRankingClipsWithoutViews", {
                        count: partialRankingData.adminPartialRankingByDateRange.clips_without_views.length,
                      })}
                    </div>
                    {(!partialRankingData.adminPartialRankingByDateRange.end_date_used || partialRankingData.adminPartialRankingByDateRange.end_date_used <= new Date().toISOString().slice(0, 10)) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-amber-600 border-amber-500/50"
                        onClick={handleSyncAndRefetch}
                        disabled={syncingViews}
                      >
                        {syncingViews ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <RefreshCw className="w-3 h-3 mr-1" />
                        )}
                        {syncingViews
                          ? t("admin.challenges.syncingViews")
                          : t("admin.challenges.syncAllViews")}
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    {t("admin.challenges.partialRankingClipsWithoutViewsDetail")}
                  </p>
                  <ul className="text-xs space-y-1">
                    {partialRankingData.adminPartialRankingByDateRange.clips_without_views.map((w) => (
                      <li key={w.clip_id} className="flex items-center gap-2">
                        <span className="text-muted-foreground">{w.participant_name || "—"}</span>
                        <a
                          href={w.clip_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline truncate max-w-[200px]"
                        >
                          {w.clip_url}
                        </a>
                        <Badge variant="outline" className="text-xs text-amber-600 border-amber-500/50">
                          {w.missing === "both"
                            ? t("admin.challenges.partialRankingMissingBoth")
                            : w.missing === "start"
                            ? t("admin.challenges.partialRankingMissingStart")
                            : t("admin.challenges.partialRankingMissingEnd")}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {partialRankingData.adminPartialRankingByDateRange.entries.length > 0 ? (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[60px]">#</TableHead>
                        <TableHead>{t("admin.challenges.participant")}</TableHead>
                        <TableHead>{t("admin.challenges.clipCount")}</TableHead>
                        <TableHead>{t("admin.challenges.totalViews")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {partialRankingData.adminPartialRankingByDateRange.entries.map((entry, i) => (
                        <TableRow key={entry.participation_id}>
                          <TableCell>
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                              i === 0 ? "bg-amber-500 text-black" :
                              i === 1 ? "bg-gray-400 text-black" :
                              i === 2 ? "bg-amber-700 text-white" :
                              "bg-muted text-muted-foreground"
                            }`}>
                              {i + 1}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">{entry.user_name || "—"}</span>
                          </TableCell>
                          <TableCell>{entry.clip_count}</TableCell>
                          <TableCell className="font-bold text-primary">
                            {parseInt(entry.total_views || "0", 10).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t("admin.challenges.partialRankingNoResults")}
                </p>
              )}
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminChallengeRankings;
